/**
 * Patenti di guida europee (modello UE, direttiva 2006/126/CE).
 *
 * Tutte le patenti UE hanno sul fronte gli stessi campi numerati, in
 * qualsiasi lingua:
 *  1. cognome            2. nome
 *  3. data e luogo di nascita
 *  4a. data di rilascio  4b. data di scadenza
 *  4c. autorita' che l'ha rilasciata
 *  5. numero della patente
 * Non c'e' una MRZ: i campi si leggono dal testo stampato, senza cifre di
 * controllo, quindi restano tutti "da controllare". La patente non riporta
 * sesso e cittadinanza.
 */

// Titoli della patente nelle lingue UE: servono a riconoscere il documento.
const TITOLI = /PATENTE|F\W?[UÜO]+\W?HRERSCHE|DRIVING|LICEN[CS]E|PERMIS|PERMISO|RIJBEWIJS|K[OÖ]RKORT|AJOKORTTI|PRAWO\s+JAZDY|CONDU[CÇ][AÃ]O|[RŘ]IDI[CČ]SK|VODI[CČ]SK|JOGOS[IÍ]TV|CONDUCERE|VOZNIŠK|VOZA[CČ]K|K[OÕ]RKORT|JUHILUBA|VAD[IĪ]T[AĀ]JA|VAIRUOTOJO|K[OÖ]REKORT|SVOJE/i;

// Il passaporto ha anche lui i campi numerati: non e' una patente. Oltre al
// titolo, che un riflesso puo' coprire, si riconosce dalle etichette.
const PASSAPORTO = /PASSPORT|PASSEPORT|REISEPASS|PASSAPORTO|PASAPORTE|PASSAPORTE|PASPOORT|PASZPORT|PASSAPORT|GIVEN\s*NAMES|VORNAMEN|GESCHLECHT|STAATSANGEH/i;

// Titoli accademici davanti al cognome (patenti austriache e tedesche), solo
// con il punto: "Di" e "De" senza punto sono parte di cognomi italiani.
const TITOLO = /^\s*(?:(?:Univ\.-Prof|Dipl\.-Ing|MMag|Mag|DDr|Dr|Prof|Ing|DI)\.\s*)+/i;

// Stato che rilascia, riconosciuto dal titolo (codice ISO a tre lettere).
const STATI = [
  [/REPUBBLICA\s+ITALIANA|PATENTE\s+DI\s+GUIDA/i, "ITA"],
  [/BUNDESREPUBLIK|DEUTSCHLAND/i, "DEU"],
  [/[OÖ]STERREICH/i, "AUT"],
  [/FRAN[CÇ]AISE|R[EÉ]PUBLIQUE/i, "FRA"],
  [/ESPA[NÑ]A|PERMISO\s+DE\s+CONDUCCI/i, "ESP"],
  [/NEDERLAND/i, "NLD"],
  [/BELGI/i, "BEL"],
  [/PORTUGU/i, "PRT"],
  [/POLSKA|PRAWO\s+JAZDY/i, "POL"],
  [/ROM[AÂ]NIA/i, "ROU"],
  [/SAN\s+MARINO/i, "SMR"],
  [/SVERIGE|K[OÖ]RKORT/i, "SWE"],
  [/SUOMI|AJOKORTTI/i, "FIN"],
  [/DANMARK|K[OØ]REKORT/i, "DNK"],
  [/LUXEMBOURG/i, "LUX"],
  [/IRELAND|[EÉ]IRE/i, "IRL"],
  [/MALTA/i, "MLT"],
  [/HRVATSKA/i, "HRV"],
  [/SLOVENIJA/i, "SVN"],
  [/SLOVENSK/i, "SVK"],
  [/[CČ]ESK/i, "CZE"],
  [/MAGYAR/i, "HUN"]
];

/**
 * Inizio di un campo numerato: "1.", "2", "4a.", "4c,", ... L'OCR scambia
 * spesso la lettera (4c letto "4e", 4a letto "4o"): si guarda soprattutto la
 * posizione nell'ordine dei campi.
 */
const CAMPO = /^\s*[.,'`]?\s*(1|2|3|4\s?[a-eo]|5)\s*(?:[.,:;)]\s*|\s+)(.+)$/i;

/** Data gg/mm/aa(aa) o gg.mm.aa(aa) in AAAA-MM-GG, null se non valida. */
function data(testo, tipo) {
  const m = String(testo || "")
    .replace(/[Oo]/g, "0")
    .replace(/[Il|]/g, "1")
    .match(/(\d{1,2})\s?[./-]\s?(\d{1,2})\s?[./-]\s?(\d{2,4})/);
  if (!m) {
    return null;
  }
  const g = parseInt(m[1], 10);
  const me = parseInt(m[2], 10);
  let a = parseInt(m[3], 10);
  if (g < 1 || g > 31 || me < 1 || me > 12) {
    return null;
  }
  if (m[3].length === 2) {
    const oggi = new Date().getFullYear() % 100;
    // Nati e rilasci non sono nel futuro; le scadenze si'.
    a += tipo === "scadenza" ? 2000 : a > oggi ? 1900 : 2000;
  }
  // Un titolare con piu' di 110 anni e' una cifra letta male.
  if (tipo === "nascita" && a < new Date().getFullYear() - 110) {
    return null;
  }
  const pad = (x) => String(x).padStart(2, "0");
  return a + "-" + pad(me) + "-" + pad(g);
}

/** Testo del campo senza la data: per il 3 resta il luogo di nascita. */
function senzaData(testo) {
  return String(testo || "")
    .replace(/\d{1,2}\s?[./-]\s?\d{1,2}\s?[./-]\s?\d{2,4}/, "")
    .trim();
}

/** "ROMA (RM)" -> { luogo: "ROMA", provincia: "RM" }. */
function luogo(testo) {
  const t = senzaData(testo)
    .replace(/[^A-Za-zÀ-ÿ'\s()-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  const m = t.match(/^(.*?)\s*\(\s*([A-Z]{2})\s*\)/i);
  if (m) {
    return { luogo: m[1].trim(), provincia: m[2].toUpperCase() };
  }
  return { luogo: t, provincia: "" };
}

/**
 * Numero della patente italiana: due lettere, sette cifre, una lettera
 * ("FI5969875E"). L'OCR legge spesso la I della sigla come 1: o al posto
 * della I ("F15885205M") o in piu' dopo di lei ("FI15969875E"). Si corregge
 * solo se il risultato ha esattamente quella forma; altrimenti (patenti
 * vecchie, altri formati) il numero resta com'e' letto.
 */
function numeroItaliano(numero) {
  const forma = /^[A-Z]{2}\d{7}[A-Z]$/;
  if (forma.test(numero)) {
    return numero;
  }
  for (const corretto of [numero.replace(/^([A-Z])1(\d{7}[A-Z])$/, "$1I$2"), numero.replace(/^([A-Z]I)1(\d{7}[A-Z])$/, "$1$2")]) {
    if (corretto !== numero && forma.test(corretto)) {
      return corretto;
    }
  }
  return numero;
}

/**
 * Legge una patente UE dalle righe dell'OCR.
 *
 * @param {string[]} righe testo riconosciuto, una riga per elemento
 * @returns {object|null} null se non sembra una patente UE
 */
export function leggiPatenteUe(righe) {
  const testo = righe.join("\n");
  const campi = {};
  // Righe con il loro campo; nella stessa riga possono esserci due campi
  // (4a e 4c): si spezza prima di ogni "4x." che segue del testo.
  const voci = [];
  righe.forEach((r, riga) => {
    r.split(/\s(?=4\s?[a-eo]?\s*[.,:])/i).forEach((p, parte) => {
      const m = p.match(CAMPO);
      let chiave = m ? m[1].replace(/\s/g, "").toLowerCase() : "";
      // 4e e 4o non esistono: sono letture sbagliate di 4c e 4a.
      chiave = { "4e": "4c", "4o": "4a" }[chiave] || chiave;
      voci.push({ riga, parte, chiave, valore: m ? m[2].trim() : p.trim() });
    });
  });
  let indice3 = voci.findIndex((v) => v.chiave === "3");
  // Numeri dei campi illeggibili: sulla patente italiana sono stampati in
  // azzurro chiaro e l'OCR li perde quasi sempre, mentre legge bene i valori.
  // Si riconoscono allora i campi dalla forma, nell'ordine fisso della
  // patente: il 3 e' la riga che comincia con una data seguita dal luogo
  // ("27/04/03 PRATO (PO)"), 4a e 4b le righe dopo che cominciano con una data.
  const inizioData = /^\W{0,3}\d{1,2}\s?[./-]\s?\d{1,2}\s?[./-]\s?\d{2,4}/;
  if (indice3 < 0) {
    indice3 = voci.findIndex((v) => !v.chiave && inizioData.test(v.valore) && /[A-Za-zÀ-ÿ]{2,}/.test(senzaData(v.valore)));
    if (indice3 >= 0) {
      voci[indice3].chiave = "3";
      voci[indice3].valore = voci[indice3].valore.replace(/^\W+/, "");
    }
  }
  if (indice3 >= 0) {
    for (const chiave of ["4a", "4b"]) {
      const v = voci.find((x, i) => i > indice3 && !x.chiave && inizioData.test(x.valore));
      if (v && !voci.some((x) => x.chiave === chiave)) {
        v.chiave = chiave;
        v.valore = v.valore.replace(/^\W+/, "");
      }
    }
  }
  voci.forEach((v, i) => {
    // "4." senza lettera dopo il 4a sulla stessa riga: e' il 4c.
    if (v.chiave === "" && /^4\s*[.,:]\s*[A-Za-zÀ-ÿ]|^4\s+[A-Za-zÀ-ÿ]/.test(v.valore) && i > 0 && voci[i - 1].chiave === "4a" && voci[i - 1].riga === v.riga) {
      v.chiave = "4c";
      v.valore = v.valore.replace(/^4\s*[.,:]?\s*/, "");
    }
    // Un "5" prima della data di nascita, senza cifre, e' il nome (2) letto male.
    if (v.chiave === "5" && indice3 >= 0 && i < indice3 && !/\d/.test(v.valore)) {
      v.chiave = "2";
    }
  });
  voci.forEach((v, i) => {
    if (v.chiave && !campi[v.chiave]) {
      campi[v.chiave] = v.valore;
    }
    // L'ente (4c) puo' continuare nella riga sotto: "Landratsamt / Musterhausen".
    const prossima = voci[i + 1];
    if (v.chiave === "4c" && prossima && !prossima.chiave && /^[A-Za-zÀ-ÿ' -]{3,}$/.test(prossima.valore)) {
      campi["4c"] += " " + prossima.valore;
    }
  });
  // Cognome e nome senza numero leggibile: sono le righe subito sopra il 3.
  if (indice3 > 0 && (!campi["1"] || !campi["2"])) {
    const sopra = voci
      .slice(0, indice3)
      .filter((v) => !v.chiave && !TITOLI.test(v.valore))
      .map((v) => v.valore.replace(/^\S{1,2}\s+/, "").trim())
      .filter((t) => /^[A-Za-zÀ-ÿ' -]{2,40}$/.test(t));
    if (!campi["2"] && sopra.length) {
      campi["2"] = sopra.pop();
    }
    if (!campi["1"] && sopra.length) {
      campi["1"] = sopra.pop();
    }
  }
  // Ente (4c) con il numero di campo illeggibile: e' il testo che resta
  // sulla riga del 4a dopo la data, tolto quel che resta del "4c.".
  if (!campi["4c"] && campi["4a"]) {
    const resto = senzaData(campi["4a"])
      .replace(/^(?:4\S{0,4}|\S{1,3})\s+/, "")
      .trim();
    if (/[A-Za-zÀ-ÿ]{2,}/.test(resto)) {
      campi["4c"] = resto;
    }
  }
  // Numero (5) senza il suo numero di campo: e' il codice di lettere e cifre
  // che segue le date, nelle righe dopo il 4a.
  if (!campi["5"]) {
    const indice4 = voci.findIndex((v) => v.chiave === "4a");
    const dopo = indice4 >= 0 ? voci.slice(indice4 + 1) : [];
    for (const v of dopo) {
      const codice = v.valore.split(/\s+/).find((t) => t.length >= 7 && t.length <= 16 && /\d/.test(t) && /[A-Z]/i.test(t) && !/[./]/.test(t));
      if (codice) {
        campi["5"] = codice;
        break;
      }
    }
  }
  if (PASSAPORTO.test(testo)) {
    return null;
  }
  const trovati = ["1", "2", "3", "4a", "4b", "5"].filter((c) => campi[c]).length;
  // Con quattro o piu' campi numerati la struttura basta a dire che e' una
  // patente; con tre serve anche il titolo, che l'OCR a volte storpia.
  if (trovati < 3 || (trovati === 3 && !TITOLI.test(testo))) {
    return null;
  }
  const stato = STATI.find(([re]) => re.test(testo));
  const nascita = luogo(campi["3"]);
  let numero = String(campi["5"] || "")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "");
  if (stato && stato[1] === "ITA") {
    numero = numeroItaliano(numero);
  }
  const pulisciNome = (s) =>
    String(s || "")
      .replace(TITOLO, "")
      .replace(/[^A-Za-zÀ-ÿ'\s-]/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  return {
    cognome: pulisciNome(campi["1"]),
    nome: pulisciNome(campi["2"]),
    dataNascita: data(campi["3"], "nascita"),
    luogoNascita: nascita.luogo,
    provinciaNascita: nascita.provincia,
    dataRilascio: data(campi["4a"], "rilascio"),
    dataScadenza: data(campi["4b"], "scadenza"),
    ente: String(campi["4c"] || "").trim(),
    numero,
    paese: stato ? stato[1] : "",
    campiLetti: trovati
  };
}

export default { leggiPatenteUe };
